import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LogoIcon from '../../common/LogoIcon';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiLock, FiUser, FiMail, FiShield, FiCheckCircle, FiAlertCircle, FiArrowRight, FiActivity } = FiIcons;

const Bootstrap = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bootstrapNeeded, setBootstrapNeeded] = useState(false);
  const [formData, setFormData] = useState({
    bootstrap_secret: '',
    email: '',
    display_name: '',
    password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch('/api/auth/bootstrap-status');
        const data = await response.json();
        if (data.status === 'success') {
          setBootstrapNeeded(data.bootstrap_needed);
        }
      } catch (err) {
        console.error("Failed to check bootstrap status");
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/auth/bootstrap-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bootstrap_secret: formData.bootstrap_secret,
          email: formData.email,
          display_name: formData.display_name,
          password: formData.password
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSuccess(true);
        setTimeout(() => navigate('/admin/login'), 3000);
      } else {
        setError(data.message || 'Bootstrap failed.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-brand-olive border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!bootstrapNeeded && !success) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center p-6">
        <div className="bg-white p-10 shadow-2xl w-full max-w-md border border-brand-olive/5 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-brand-olive" />
          <LogoIcon className="w-20 h-20 mx-auto mb-6 opacity-20 grayscale" />
          <h1 className="font-serif text-xl uppercase tracking-widest text-brand-olive font-bold mb-4">Bootstrap Completed</h1>
          <div className="p-4 bg-brand-cream/50 text-brand-olive/60 text-[10px] font-bold uppercase tracking-widest leading-relaxed border border-brand-olive/5 mb-6">
            Initial developer account has already been created. Further bootstrap attempts are restricted.
          </div>
          <Link to="/admin/login" className="inline-flex items-center gap-2 text-brand-olive font-bold uppercase tracking-widest text-[10px] hover:underline">
            Proceed to Login <SafeIcon icon={FiArrowRight} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center p-6">
      <div className="bg-white p-10 shadow-2xl w-full max-w-md border border-brand-olive/5 transition-all relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-brand-olive" />
        
        <div className="text-center mb-8">
          <LogoIcon className="w-20 h-20 mx-auto mb-6 rounded-full shadow-lg" />
          <h1 className="font-serif text-2xl uppercase tracking-widest text-brand-olive font-bold">System Bootstrap</h1>
          <p className="text-brand-olive/50 text-[10px] font-black uppercase tracking-widest mt-2">Initialize Developer Access</p>
        </div>

        {success ? (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-3xl animate-bounce">
                <SafeIcon icon={FiCheckCircle} />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Success!</h2>
            <p className="text-gray-600 text-sm">Developer account created. Redirecting to login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 text-[10px] font-bold uppercase tracking-widest border-l-4 border-red-500 flex items-center gap-3">
                <SafeIcon icon={FiAlertCircle} className="text-sm shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-1">
              <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-brand-olive/60">
                <SafeIcon icon={FiActivity} /> Bootstrap Secret
              </label>
              <input 
                name="bootstrap_secret"
                type="password"
                value={formData.bootstrap_secret}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-brand-cream/30 border border-brand-olive/10 focus:border-brand-olive outline-none transition-all text-brand-olive font-bold text-sm"
                placeholder="Enter system secret"
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-brand-olive/60">
                  <SafeIcon icon={FiUser} /> Full Name
                </label>
                <input 
                  name="display_name"
                  type="text"
                  value={formData.display_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-brand-cream/30 border border-brand-olive/10 focus:border-brand-olive outline-none transition-all text-brand-olive font-bold text-sm"
                  placeholder="e.g. System Developer"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-brand-olive/60">
                  <SafeIcon icon={FiMail} /> Email Address
                </label>
                <input 
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-brand-cream/30 border border-brand-olive/10 focus:border-brand-olive outline-none transition-all text-brand-olive font-bold text-sm"
                  placeholder="admin@example.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-brand-olive/60">
                <SafeIcon icon={FiLock} /> Password
              </label>
              <input 
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-brand-cream/30 border border-brand-olive/10 focus:border-brand-olive outline-none transition-all text-brand-olive font-bold text-sm"
                placeholder="Min 12 characters"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-brand-olive/60">
                <SafeIcon icon={FiShield} /> Confirm Password
              </label>
              <input 
                name="confirm_password"
                type="password"
                value={formData.confirm_password}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-brand-cream/30 border border-brand-olive/10 focus:border-brand-olive outline-none transition-all text-brand-olive font-bold text-sm"
                placeholder="Repeat password"
                required
              />
            </div>

            <button 
              type="submit" 
              disabled={submitting} 
              className="w-full py-4 bg-brand-olive text-white font-bold uppercase tracking-[0.2em] text-[10px] transition-all duration-300 shadow-xl hover:bg-brand-dark hover:shadow-2xl active:scale-95 disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
            >
              {submitting ? 'Initializing...' : 'Bootstrap Developer'}
            </button>
            
            <p className="text-[8px] text-center text-gray-400 font-bold uppercase tracking-widest leading-relaxed mt-4">
              This is a one-time operation. Ensure all details are correct before initializing.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default Bootstrap;